import { MutableRefObject, useEffect, useRef, useState } from "react"
import WebViewer from "@pdftron/webviewer"
import type { WebViewerInstance } from "@pdftron/webviewer"
import { useViewerStore } from "store/viewer.store"

const licenseKey = import.meta.env.VITE_WEBVIEWER_KEY

type Props = {
	fileLink: string | undefined
	previewRef: MutableRefObject<null>
	isModalOpen: boolean
}

export const useWebviewer = ({ fileLink, previewRef, isModalOpen }: Props) => {
	const instanceRef = useRef<WebViewerInstance | null>(null)
	// We register this preview's instance via setPreviewInstance, which publishes ONLY
	// the navigation service (the AI Agent's page-reference navigation reads it) and the
	// store instance (DocumentModal's resize handling reads it). It intentionally does
	// NOT register AnnotationService — that singleton belongs to the custom quotation
	// editor; pointing it at the read-only preview hijacks it (promoteUserToAdmin, cached
	// annotations re-rendering on the preview, editor losing listeners on reopen). See
	// PR #1118, which over-corrected by dropping registration entirely and so broke the
	// AI Agent "PDF viewer not initialized" navigation.
	const { setPreviewInstance, clearPreviewInstance, openFeedbackPanel } = useViewerStore()
	const loadingTaskRef = useRef<Promise<unknown> | null>(null)
	const [isInitialized, setIsInitialized] = useState(false)

	// Initialize WebViewer once. WebViewer() is async, so instanceRef stays null until
	// it resolves. The loadingTaskRef guard prevents a second WebViewer() call while
	// that init is still in flight — without it, a fileLink that arrives at modal-open
	// time (as in the Documents tab, where the download URL is fetched on click) re-runs
	// this effect before instanceRef is set and spins up a duplicate viewer, leaving the
	// document area blank. The Quotations tab prefetches the URL so fileLink is stable
	// and never hit this race.
	useEffect(() => {
		if (!previewRef.current || instanceRef.current || loadingTaskRef.current) {
			return
		}

		loadingTaskRef.current = WebViewer(
			{
				licenseKey: licenseKey,
				path: "/webviewer",
				initialDoc: fileLink,
				css: "/webviewer.css",
				disabledElements: [
					"toolbarGroup-Annotate",
					"toolbarGroup-Insert",
					"toolbarGroup-Edit",
					"toolbarGroup-FillAndSign",
					"toolbarGroup-Forms",
					"notesPanelToggle",
				],
				// Production stability options
				enableOptimizedWorkers: true,
				fullAPI: false,
				useDownloader: false,
				// disableVirtualDisplayMode: true, // Disable virtual display mode
			},
			previewRef.current,
		)
			.then((viewerInstance: WebViewerInstance) => {
				// Expose the WebViewer instance globally so automated tests (e.g. Playwright)
				// can verify annotation selection/highlight after navigation. No effect on app
				// behavior — see https://docs.apryse.com/web/faq/react-global-instance
				const globalWindow = window as unknown as { __wvInstance?: WebViewerInstance }
				globalWindow.__wvInstance = viewerInstance

				instanceRef.current = viewerInstance
				// Register so the AI Agent reference navigation (ViewerNavigationService)
				// and DocumentModal's resize handling (store instance) can reach the viewer.
				setPreviewInstance(viewerInstance)

				const { UI } = viewerInstance

				const customButton = new UI.Components.CustomButton({
					title: "Share feedback",
					label: "Share feedback",
					dataElement: "feedbackButton",
					onClick: () => {
						openFeedbackPanel()
					},
				})

				const defaultHeader = UI.getModularHeader("default-top-header")
				const items = defaultHeader.getItems()
				const searchIndex = items.findIndex(
					(item: { dataElement?: string }) => item.dataElement === "searchPanelToggle",
				)

				items.splice(searchIndex > -1 ? searchIndex : items.length, 0, customButton)
				defaultHeader.setItems(items)

				setIsInitialized(true)
			})
			.catch((error: Error) => {
				console.error("WebViewer initialization failed", error)
				loadingTaskRef.current = null
			})
		// isModalOpen is a dep so opening the modal triggers init when fileLink is already
		// stable (Quotations prefetches the URL); the guard above makes the re-runs safe.
	}, [fileLink, previewRef, setPreviewInstance, openFeedbackPanel, isModalOpen])

	// Release the navigation singleton + store instance when the preview unmounts, so a
	// disposed viewer is never left registered for the next consumer. We clear on unmount
	// (not on modal close) because the instance is created once and reused across reopens —
	// the init effect's .then (where setPreviewInstance runs) does not fire again.
	useEffect(() => {
		return () => {
			clearPreviewInstance()
		}
	}, [clearPreviewInstance])

	// Load the document once the instance is ready, and reload it on modal reopen
	// (closeDocument() runs on close below). Gating on the isInitialized state — rather
	// than the instanceRef, which can't trigger a re-run — guarantees the load fires
	// even when fileLink was still undefined at construction time.
	useEffect(() => {
		if (instanceRef.current && fileLink && isInitialized && isModalOpen) {
			instanceRef.current.UI.loadDocument(fileLink)
		}
	}, [fileLink, isInitialized, isModalOpen])

	// Handle cleanup when the modal is closed
	useEffect(() => {
		if (!isModalOpen && instanceRef.current) {
			const { Core } = instanceRef.current
			if (Core && Core.documentViewer) {
				try {
					Core.documentViewer.closeDocument()
				} catch (error) {
					console.warn("Could not close document:", error)
				}
			}
		}
	}, [isModalOpen])
}
